import {createAsyncThunk} from '@reduxjs/toolkit';
import {apiFetch, getWorkerApiUrl} from '../../lib/api';
import {getJWTToken} from '../../lib/auth-token';
import {debugLog} from '../../lib/debug';
import {PRO_FEATURES, PRO_MONTHLY_NAME, PRO_YEARLY_NAME} from '../../domain/plans';
import type {PaddleProducts, Subscription} from '../../domain/types';

/**
 * Every Paddle read went through three hand-rolled copies on the marketing site
 * (DashboardPage / ProfilePage / landing Pricing). This is the single implementation.
 *
 * The worker answers in snake_case; the mapping to camelCase happens here so nothing downstream
 * ever sees `billing_cycle` or `management_urls`.
 */

/**
 * Pricing. Public endpoint — no JWT, so the landing page can render it logged out.
 */
export const fetchPaddleProducts = createAsyncThunk(
  'subscription/fetchProducts',
  async (_, {rejectWithValue}) => {
    try {
      const response = await apiFetch(`${getWorkerApiUrl()}/rest/v1/paddle/products`);

      if (!response.ok) {
        throw new Error(`Failed to fetch pricing (${response.status})`);
      }

      const data = await response.json();
      const products = data.products;

      if (!products?.monthly?.price_id || !products?.yearly?.price_id) {
        throw new Error('Pricing response is missing monthly/yearly price ids');
      }

      const mapped: PaddleProducts = {
        monthly: {
          name: PRO_MONTHLY_NAME,
          priceId: products.monthly.price_id,
          price: products.monthly.amount,
          currency: products.monthly.currency,
          billingCycle: 'monthly',
          features: PRO_FEATURES,
        },
        yearly: {
          name: PRO_YEARLY_NAME,
          priceId: products.yearly.price_id,
          price: products.yearly.amount,
          currency: products.yearly.currency,
          billingCycle: 'yearly',
          features: PRO_FEATURES,
        },
      };

      debugLog('[Subscription] Products fetched:', mapped);
      return mapped;
    } catch (error: any) {
      console.error('[Subscription] Failed to fetch products:', error);
      return rejectWithValue(error?.message || String(error));
    }
  },
  {
        // The landing page and the account page can both mount a pricing container in the same
        // tick; without this they each fire a request because neither has seen the other's pending.
    condition: (_, {getState}) => (getState() as any).subscription.api.products.status !== 'pending',
  }
);

/**
 * The signed-in user's Paddle subscriptions. Returns [] when signed out rather than rejecting,
 * so the pricing cards can render their logged-out state without an error toast.
 */
export const fetchSubscriptions = createAsyncThunk(
  'subscription/fetchSubscriptions',
  async (_, {rejectWithValue}) => {
    try {
      const jwt = await getJWTToken();
      if (!jwt) {
        return [] as Subscription[];
      }

      const response = await apiFetch(`${getWorkerApiUrl()}/rest/v1/paddle/subscriptions`, {
        headers: {Authorization: `Bearer ${jwt}`},
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch subscriptions (${response.status})`);
      }

      const data = await response.json();
      const subscriptions: Subscription[] = (data.subscriptions || []).map((sub: any) => ({
        id: sub.id,
        status: sub.status,
        billingCycle: sub.billing_cycle
          ? {frequency: sub.billing_cycle.frequency, interval: sub.billing_cycle.interval}
          : undefined,
        nextBilledAt: sub.next_billed_at,
        managementUrls: {
          updatePaymentMethod: sub.management_urls?.update_payment_method,
          cancel: sub.management_urls?.cancel,
        },
      }));

      debugLog('[Subscription] Subscriptions fetched:', subscriptions);
      return subscriptions;
    } catch (error: any) {
      console.error('[Subscription] Failed to fetch subscriptions:', error);
      return rejectWithValue(error?.message || String(error));
    }
  },
  {
    condition: (_, {getState}) => (getState() as any).subscription.api.subscriptions.status !== 'pending',
  }
);

/**
 * Get-or-create the Paddle customer for the signed-in user. Needed before opening checkout so the
 * overlay is keyed by customer id.
 */
export const createPaddleCustomer = createAsyncThunk(
  'subscription/createCustomer',
  async ({email, userId}: { email: string; userId: string }, {rejectWithValue}) => {
    try {
      const jwt = await getJWTToken();
      if (!jwt) {
        throw new Error('You must be signed in to check out');
      }

      const response = await apiFetch(`${getWorkerApiUrl()}/rest/v1/paddle/customer`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({email, userId}),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to create Paddle customer (${response.status})`);
      }

      const data = await response.json();
      const customerId = data.customer_id;

      if (!customerId) {
        throw new Error('Paddle customer response is missing customer_id');
      }

      return customerId as string;
    } catch (error: any) {
      console.error('[Subscription] Failed to create customer:', error);
      return rejectWithValue(error?.message || String(error));
    }
  }
);
